import {
  ActionCreatorWithPayload,
  createSlice,
  Dispatch,
  Draft,
  PayloadAction,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
} from "@reduxjs/toolkit";
import { db } from "firebase-config";
import { doc, onSnapshot } from "firebase/firestore";

export type TrackingStatus = "loaded" | "loading";
export interface ITrackingSlice<TModel, TTrackingDetails> {
  data: { [id: string]: TModel | undefined };
  trackingDetails: { [id: string]: TTrackingDetails | undefined };
}

export const initialTrackingState = {
  data: {},
  trackingDetails: {},
};

export const isDataTracked = <TModel, TTrackingDetails>(
  state: ITrackingSlice<TModel, TTrackingDetails>,
  id: string
) => {
  return state.trackingDetails[id] !== undefined;
};

export const hasDataLoaded = <TModel>(
  state: ITrackingSlice<TModel, TrackingStatus>,
  id: string
) => {
  return state.trackingDetails[id] === "loaded";
};

export const createTrackingSlice = <
  TTrackingDetails,
  T extends ITrackingSlice<TModel, TTrackingDetails>,
  Reducers extends SliceCaseReducers<T>,
  TModel
>(
  {
    name,
    initialState,
    reducers,
  }: {
    name: string;
    initialState: T;
    reducers: ValidateSliceCaseReducers<T, Reducers>;
  },
  getTrackingStatus: (
    details: Draft<TTrackingDetails> | undefined
  ) => TrackingStatus | undefined,
  newTrackingDetails: (
    status: TrackingStatus,
    data?: Draft<TModel>
  ) => Draft<TTrackingDetails>
) => {
  return createSlice({
    name: name.toLowerCase(),
    initialState,
    reducers: {
      track: (
        state,
        action: PayloadAction<{
          ids: string[];
        }>
      ) => {
        const { ids } = action.payload;
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i];
          if (!isDataTracked(state, id)) {
            state.trackingDetails[id] = newTrackingDetails("loading");
          }
        }
      },
      update: (
        state,
        action: PayloadAction<{
          id: string;
          data: Draft<TModel> | undefined;
        }>
      ) => {
        const { id, data } = action.payload;
        state.data[id] = data;
        if (getTrackingStatus(state.trackingDetails[id]) !== "loaded") {
          state.trackingDetails[id] = newTrackingDetails("loaded", data);
        }
      },
      ...reducers,
    },
  });
};

export const startTracking = <
  T1 extends string,
  T2 extends string,
  TFsModel,
  TModel
>(
  ids: string[],
  collectionName: string,
  map: (fsModel: TFsModel, id: string) => TModel | undefined,
  dispatch: Dispatch,
  track: ActionCreatorWithPayload<
    {
      ids: string[];
    },
    T1
  >,
  update: ActionCreatorWithPayload<
    {
      id: string;
      data: TModel | undefined;
    },
    T2
  >
) => {
  // console.log(`startTracking: ${collectionName} x ${ids.length}`);
  dispatch(
    track({
      ids,
    })
  );

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    onSnapshot(doc(db, collectionName, id), (docSnapshot) => {
      const data = docSnapshot.exists()
        ? map(docSnapshot.data() as TFsModel, docSnapshot.id)
        : undefined;
      // console.log(`onSnapshot: ${collectionName} ${id}`);

      dispatch(
        update({
          id,
          data,
        })
      );
    });
  }
};
